import { useState, useEffect, useCallback } from 'react';

type DebounceFunction<T extends any[]> = (...args: T) => void;

const useDebounce = <T extends any[]>(
  callback: DebounceFunction<T>,
  delay: number,
): DebounceFunction<T> => {
  const [timeoutId, setTimeoutId] = useState<NodeJS.Timeout | null>(null);

  const debouncedFunction = useCallback(
    (...args: T) => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      const newTimeoutId = setTimeout(() => {
        callback(...args);
      }, delay);
      setTimeoutId(newTimeoutId);
    },
    [callback, delay, timeoutId],
  );

  useEffect(() => {
    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [timeoutId]);

  return debouncedFunction;
};

export default useDebounce;
